import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import { PropertyFilters, InvestmentAssumptions } from '../types/filters';

interface FiltersStateType {
  filters: PropertyFilters;
  assumptions: InvestmentAssumptions;
}

interface FiltersActionsType {
  setFilters: (filters: PropertyFilters) => void;
  updateFilter: (key: keyof PropertyFilters, value: any) => void;
  clearFilters: () => void;
  updateAssumption: (key: keyof InvestmentAssumptions, value: any) => void;
  resetAssumptions: () => void;
}

// Separate contexts to prevent unnecessary re-renders
const FiltersStateContext = createContext<FiltersStateType | undefined>(undefined);
const FiltersActionsContext = createContext<FiltersActionsType | undefined>(undefined);

const DEFAULT_FILTERS: PropertyFilters = {
  status: ['for_sale'], // Default to show active for sale listings
  property_type: ['single_family', 'multi_family'],
};

const DEFAULT_ASSUMPTIONS: InvestmentAssumptions = {
  down_payment_pct: 0.20,
  interest_rate: 0.07,
  loan_term_years: 30,
  closing_costs_fixed: 3500,
  closing_costs_mode: 'fixed' as const,
  escrow_pct: 0.03,
  reserves_amount: 5000,
  property_tax_rate: 0.011,
  insurance_pct: 0.01,
  maintenance_pct: 0.01,
  property_mgmt_pct: 0.10,
  vacancy_rate: 0.08,
  capex_reserve_pct: 0.10,
  pmi_rate: 0.0,
};

export const FiltersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<PropertyFilters>(DEFAULT_FILTERS);
  const [assumptions, setAssumptions] = useState<InvestmentAssumptions>(DEFAULT_ASSUMPTIONS);

  // Actions are memoized once and never change - prevents re-renders
  const actions = useMemo<FiltersActionsType>(() => ({
    setFilters,
    updateFilter: (key: keyof PropertyFilters, value: any) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value === '' ? undefined : value,
      }));
    },
    clearFilters: () => {
      setFilters(DEFAULT_FILTERS);
    },
    updateAssumption: (key: keyof InvestmentAssumptions, value: any) => {
      setAssumptions((prev) => ({
        ...prev,
        [key]: value === '' ? DEFAULT_ASSUMPTIONS[key] : value,
      }));
    },
    resetAssumptions: () => {
      setAssumptions(DEFAULT_ASSUMPTIONS);
    }
  }), []); // Empty deps - actions never change

  // State changes frequently but only consumed by components that need it
  const state = useMemo<FiltersStateType>(() => ({
    filters,
    assumptions
  }), [filters, assumptions]);

  return (
    <FiltersActionsContext.Provider value={actions}>
      <FiltersStateContext.Provider value={state}>
        {children}
      </FiltersStateContext.Provider>
    </FiltersActionsContext.Provider>
  );
};

// Hook for components that only need actions (won't re-render on state changes)
export const useFiltersActions = () => {
  const context = useContext(FiltersActionsContext);
  if (!context) {
    throw new Error('useFiltersActions must be used within FiltersProvider');
  }
  return context;
};

// Hook for components that only need state (will re-render on state changes)
export const useFiltersState = () => {
  const context = useContext(FiltersStateContext);
  if (!context) {
    throw new Error('useFiltersState must be used within FiltersProvider');
  }
  return context;
};

// Hook for components that need both (will re-render on state changes)
export const useFilters = () => {
  const state = useFiltersState();
  const actions = useFiltersActions();
  return { ...state, ...actions };
};
